import React, { useEffect, useState, useCallback, useRef, useContext, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  FileText, Clock, RotateCcw, CheckCircle2, XCircle,
  ChevronDown, ChevronLeft, ChevronRight, Calendar, X, ExternalLink,
  Send, Settings2, ChevronUp, Loader2, LayoutDashboard, List, Search,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, PieChart, Pie, Cell, ResponsiveContainer,
} from 'recharts';
import { YearContext } from '../components/Layout/Layout';
import ImportExport from '../components/Common/ImportExport';
import DataTable from '../components/Common/DataTable';
import EmailNotificationSetup    from '../components/Common/EmailNotificationSetup';
import { grievancesAPI } from '../services/api';
import { PageLoader } from '../components/Common/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// ── Colours ────────────────────────────────────────────────
const STATUS_CFG = {
  new:         { label:'New',         color:'#3b82f6', bg:'bg-blue-50',   text:'text-blue-700',   border:'border-blue-200',   Icon:FileText,     desc:'New entries awaiting action'        },
  in_progress: { label:'In Progress', color:'#eab308', bg:'bg-yellow-50', text:'text-yellow-700', border:'border-yellow-200', Icon:Clock,        desc:'Items actively being handled'       },
  reopened:    { label:'Reopened',    color:'#f97316', bg:'bg-orange-50', text:'text-orange-700', border:'border-orange-200', Icon:RotateCcw,    desc:'Requires additional attention'      },
  resolved:    { label:'Resolved',    color:'#22c55e', bg:'bg-green-50',  text:'text-green-700',  border:'border-green-200',  Icon:CheckCircle2, desc:'Issue fixed and marked as resolved' },
  closed:      { label:'Closed',      color:'#a855f7', bg:'bg-purple-50', text:'text-purple-700', border:'border-purple-200', Icon:XCircle,      desc:'All actions completed and closed'   },
};
const BAR_COLORS  = { new:'#3b82f6', in_progress:'#eab308', reopened:'#f97316', resolved:'#22c55e', closed:'#a855f7' };
const PRIO_COLORS = { low:'#22c55e', medium:'#3b82f6', high:'#f97316', critical:'#ef4444' };
const PRIO_LABELS = { low:'Low Priority', medium:'Medium Priority', high:'High Priority', critical:'Critical' };
const MONTHS_LONG  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ── Date helpers ───────────────────────────────────────────
const fmtISO  = d => d.toISOString().split('T')[0];
const fmtDisp = d => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${parseInt(day)} ${MONTHS_SHORT[parseInt(m)-1]}, ${y}`;
};
const fmtMMDD = d => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${m}/${day}/${y}`;
};
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';

const PRESETS = ['Today','Yesterday','Last 7 Days','Last 30 Days','This Month','Last Month','Custom Range'];

function getPresetDates(preset) {
  const t = new Date();
  switch (preset) {
    case 'Today':         return { start:fmtISO(t), end:fmtISO(t) };
    case 'Yesterday':   { const d=new Date(t); d.setDate(d.getDate()-1); return {start:fmtISO(d),end:fmtISO(d)}; }
    case 'Last 7 Days': { const d=new Date(t); d.setDate(d.getDate()-6); return {start:fmtISO(d),end:fmtISO(t)}; }
    case 'Last 30 Days':{ const d=new Date(t); d.setDate(d.getDate()-29); return {start:fmtISO(d),end:fmtISO(t)}; }
    case 'This Month':  { const d=new Date(t.getFullYear(),t.getMonth(),1); return {start:fmtISO(d),end:fmtISO(t)}; }
    case 'Last Month':  {
      const s=new Date(t.getFullYear(),t.getMonth()-1,1);
      const e=new Date(t.getFullYear(),t.getMonth(),0);
      return {start:fmtISO(s),end:fmtISO(e)};
    }
    default: return null;
  }
}

function detectPreset(start, end) {
  if (!start || !end) return 'Custom Range';
  for (const p of PRESETS) {
    if (p === 'Custom Range') continue;
    const d = getPresetDates(p);
    if (d && d.start === start && d.end === end) return p;
  }
  return 'Custom Range';
}

const defaultRange = () => ({ preset:'Last 30 Days', ...getPresetDates('Last 30 Days') });

// ── Calendar day grid ──────────────────────────────────────
function getCalendarDays(year, month) {
  const firstDay  = new Date(year, month-1, 1).getDay();
  const totalDays = new Date(year, month, 0).getDate();
  const prevM = month===1?12:month-1, prevY = month===1?year-1:year;
  const prevTotal = new Date(prevY, prevM, 0).getDate();
  const nextM = month===12?1:month+1, nextY = month===12?year+1:year;
  const days = [];
  for (let i=firstDay-1;i>=0;i--) {
    const d=prevTotal-i;
    days.push({date:`${prevY}-${String(prevM).padStart(2,'0')}-${String(d).padStart(2,'0')}`,n:d,cur:false});
  }
  for (let d=1;d<=totalDays;d++) {
    days.push({date:`${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`,n:d,cur:true});
  }
  let nd=1;
  while (days.length<42) {
    days.push({date:`${nextY}-${String(nextM).padStart(2,'0')}-${String(nd).padStart(2,'0')}`,n:nd,cur:false});
    nd++;
  }
  return days;
}

// ── Month calendar component ───────────────────────────────
function MonthGrid({ year, month, onPrev, onNext, showPrev, showNext,
                     onMonthChange, onYearChange,
                     rangeStart, rangeEnd, hovered, selecting,
                     onDayClick, onDayHover }) {
  const today = fmtISO(new Date());
  const days  = getCalendarDays(year, month);
  const YEAR_OPTS = Array.from({length:12}, (_,i) => new Date().getFullYear()-5+i);

  // Effective end for preview while user is selecting second date
  const effEnd = rangeEnd || (selecting && hovered && hovered >= rangeStart ? hovered : null);

  const getCellClasses = ({ date, cur }) => {
    const isStart = date === rangeStart;
    const isEnd   = date === effEnd;
    const inRange = rangeStart && effEnd && date > rangeStart && date < effEnd;
    const isToday = date === today && cur;

    // Outer div: shows continuous range background
    let outer = 'h-8 flex items-center justify-center ';
    if (isStart && effEnd) outer += 'bg-blue-100 rounded-l-full ';
    else if (isEnd && rangeStart) outer += 'bg-blue-100 rounded-r-full ';
    else if (inRange)  outer += 'bg-blue-100 ';
    else outer += '';

    // Inner div: circle for selected dates, hover for others
    let inner = 'w-8 h-8 flex items-center justify-center text-xs rounded-full transition-colors ';
    if (!cur) inner += 'text-slate-300 cursor-default ';
    else if (isStart || isEnd) inner += 'bg-primary-600 text-white font-semibold cursor-pointer ';
    else if (isToday) inner += 'font-bold text-primary-600 hover:bg-slate-100 cursor-pointer ';
    else inner += 'text-slate-700 hover:bg-slate-100 cursor-pointer ';

    return { outer, inner };
  };

  return (
    <div style={{minWidth:230}}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <button onClick={showPrev ? onPrev : undefined}
          className={`p-1 rounded hover:bg-slate-100 transition-colors ${!showPrev?'invisible':''}`}>
          <ChevronLeft size={16} className="text-slate-500" />
        </button>
        <div className="flex items-center gap-1">
          <select value={month} onChange={e=>onMonthChange(parseInt(e.target.value))}
            className="text-sm font-semibold text-slate-700 bg-transparent border-0 cursor-pointer focus:outline-none hover:text-primary-600 pr-1">
            {MONTHS_LONG.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={e=>onYearChange(parseInt(e.target.value))}
            className="text-sm font-semibold text-slate-700 bg-transparent border-0 cursor-pointer focus:outline-none hover:text-primary-600">
            {YEAR_OPTS.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <button onClick={showNext ? onNext : undefined}
          className={`p-1 rounded hover:bg-slate-100 transition-colors ${!showNext?'invisible':''}`}>
          <ChevronRight size={16} className="text-slate-500" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-1">
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d=>(
          <div key={d} className="text-center text-[11px] text-slate-400 font-medium py-1">{d}</div>
        ))}
      </div>

      {/* Days */}
      <div className="grid grid-cols-7">
        {days.map(day => {
          const {outer, inner} = getCellClasses(day);
          return (
            <div key={day.date} className={outer}
              onClick={() => day.cur && onDayClick(day.date)}
              onMouseEnter={() => day.cur && selecting && onDayHover(day.date)}
              onMouseLeave={() => selecting && onDayHover(null)}>
              <div className={inner}>{day.n}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Dual-month Date Range Picker ───────────────────────────
function DateRangePicker({ value, onChange }) {
  const [open,      setOpen]      = useState(false);
  const [dropPos,   setDropPos]   = useState({top:0,left:0});
  const [tempStart, setTempStart] = useState(null);
  const [tempEnd,   setTempEnd]   = useState(null);
  const [selecting, setSelecting] = useState(false);  // true = start clicked, waiting for end
  const [hovered,   setHovered]   = useState(null);
  const [leftYear,  setLeftYear]  = useState(() => new Date().getFullYear());
  const [leftMonth, setLeftMonth] = useState(() => new Date().getMonth() + 1);
  const btnRef = useRef(null);

  const rightMonth = leftMonth===12 ? 1  : leftMonth+1;
  const rightYear  = leftMonth===12 ? leftYear+1 : leftYear;

  const prevMonth = () => {
    if (leftMonth===1) { setLeftMonth(12); setLeftYear(y=>y-1); }
    else setLeftMonth(m=>m-1);
  };
  const nextMonth = () => {
    if (leftMonth===12) { setLeftMonth(1); setLeftYear(y=>y+1); }
    else setLeftMonth(m=>m+1);
  };

  const openPicker = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const pickerW = 760;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - pickerW - 8));
      const spaceBelow = window.innerHeight - r.bottom;
      const top = spaceBelow < 440 ? Math.max(8, r.top - 440) : r.bottom + 4;
      setDropPos({top, left});
    }
    // Initialise temp range from current value
    setTempStart(value.start || null);
    setTempEnd(value.end || null);
    setSelecting(false);
    setHovered(null);
    // Show the current start month
    if (value.start) {
      const d = new Date(value.start + 'T00:00:00');
      setLeftYear(d.getFullYear());
      setLeftMonth(d.getMonth()+1);
    }
    setOpen(true);
  };

  const handlePreset = preset => {
    if (preset === 'Custom Range') {
      // Just clear so user picks manually in calendar
      setTempStart(null); setTempEnd(null); setSelecting(false);
      return;
    }
    const dates = getPresetDates(preset);
    if (dates) {
      // Apply immediately and close for preset options
      onChange({ preset, ...dates });
      setOpen(false);
    }
  };

  const handleDayClick = date => {
    if (!selecting) {
      // First click: set start
      setTempStart(date);
      setTempEnd(null);
      setSelecting(true);
      setHovered(null);
    } else {
      // Second click: set end (swap if needed)
      const [s,e] = date < tempStart ? [date,tempStart] : [tempStart,date];
      setTempStart(s);
      setTempEnd(e);
      setSelecting(false);
      setHovered(null);
    }
  };

  const handleApply = () => {
    if (tempStart && tempEnd) {
      const preset = detectPreset(tempStart, tempEnd);
      onChange({ preset, start:tempStart, end:tempEnd });
      setOpen(false);
    }
  };

  const close = () => setOpen(false);

  const activePreset = detectPreset(value.start, value.end);
  const display = value.start && value.end
    ? `${fmtDisp(value.start)} - ${fmtDisp(value.end)}`
    : 'Select range';

  return (
    <div className="inline-flex items-center gap-2">
      <span className="text-xs font-medium text-slate-500 whitespace-nowrap">Creation Date</span>
      <button ref={btnRef} onClick={() => open ? close() : openPicker()}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg
                   text-xs text-slate-700 hover:border-primary-300 hover:bg-primary-50/40
                   transition-colors shadow-sm whitespace-nowrap select-none">
        <Calendar size={12} className="text-slate-400 flex-shrink-0" />
        <span className="font-medium">{display}</span>
        <ChevronDown size={11} className={`text-slate-400 transition-transform duration-150 ${open?'rotate-180':''}`} />
      </button>

      {open && ReactDOM.createPortal(
        <>
          {/* Backdrop */}
          <div style={{position:'fixed',inset:0,zIndex:99998}} onClick={close} />

          {/* Picker panel */}
          <div onClick={e=>e.stopPropagation()}
            style={{position:'fixed',top:dropPos.top,left:dropPos.left,zIndex:99999,width:760}}
            className="bg-white rounded-xl shadow-2xl border border-slate-200 flex overflow-hidden">

            {/* Left: preset list */}
            <div className="w-40 border-r border-slate-100 py-2 flex-shrink-0">
              {PRESETS.map(p=>(
                <button key={p} onClick={()=>handlePreset(p)}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors
                    ${activePreset===p
                      ? 'bg-primary-600 text-white font-semibold'
                      : 'text-slate-700 hover:bg-slate-50'
                    }`}>
                  {p}
                </button>
              ))}
            </div>

            {/* Right: dual calendars */}
            <div className="flex-1 p-5">
              <div className="flex gap-6">
                <MonthGrid
                  year={leftYear} month={leftMonth}
                  showPrev onPrev={prevMonth}
                  onMonthChange={m=>setLeftMonth(m)}
                  onYearChange={y=>setLeftYear(y)}
                  rangeStart={tempStart} rangeEnd={tempEnd}
                  hovered={hovered} selecting={selecting}
                  onDayClick={handleDayClick}
                  onDayHover={setHovered}
                />
                <div className="w-px bg-slate-100 flex-shrink-0" />
                <MonthGrid
                  year={rightYear} month={rightMonth}
                  showNext onNext={nextMonth}
                  onMonthChange={m=>{
                    const nl = m===1?12:m-1;
                    const ny = m===1?rightYear-1:rightYear;
                    setLeftMonth(nl); setLeftYear(ny);
                  }}
                  onYearChange={y=>{ setLeftYear(leftMonth===12?y-1:y); }}
                  rangeStart={tempStart} rangeEnd={tempEnd}
                  hovered={hovered} selecting={selecting}
                  onDayClick={handleDayClick}
                  onDayHover={setHovered}
                />
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                <span className="text-sm text-slate-600 font-medium">
                  {tempStart
                    ? tempEnd
                      ? `${fmtMMDD(tempStart)} - ${fmtMMDD(tempEnd)}`
                      : `${fmtMMDD(tempStart)} - ...`
                    : <span className="text-slate-400 italic text-xs">Click a start date</span>
                  }
                </span>
                <div className="flex gap-2">
                  <button onClick={close}
                    className="px-4 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-600 transition-colors">
                    Cancel
                  </button>
                  <button onClick={handleApply} disabled={!tempStart||!tempEnd}
                    className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-40 transition-colors font-medium">
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// ── Grievance Detail Modal ─────────────────────────────────
function GrievanceModal({ grievanceId, onClose }) {
  const [detail,  setDetail]  = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!grievanceId) return;
    setLoading(true); setDetail(null);
    grievancesAPI.getDetail(grievanceId)
      .then(r => setDetail(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [grievanceId]);

  const statusCfg  = STATUS_CFG[detail?.status] || STATUS_CFG.new;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[99990] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/60 flex-shrink-0">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-slate-400 font-mono">{loading?'…':detail?.grievance_no||'—'}</p>
            <h3 className="text-base font-bold text-slate-800 mt-0.5 pr-4 line-clamp-2">
              {loading ? 'Loading…' : detail?.subject||'—'}
            </h3>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
            {detail?.status && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full text-white"
                style={{background:statusCfg.color}}>
                {statusCfg.label}
              </span>
            )}
            <button onClick={onClose}
              className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors text-slate-500">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="p-6 space-y-5">
              {/* Info grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['Category',      detail?.category],
                  ['Student Name',  detail?.student_name],
                  ['Class-Section', detail?.class_section],
                  ['Assignee',      detail?.assignee],
                  ['Priority',      detail?.priority],
                  ['Raised Date',   fmtDate(detail?.raised_date)],
                  ['Closed Date',   fmtDate(detail?.resolved_date)],
                ].filter(([,v])=>v).map(([label,val])=>(
                  <div key={label} className="bg-slate-50 rounded-xl p-3">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
                    <p className="text-sm font-medium text-slate-700 capitalize">{val}</p>
                  </div>
                ))}
              </div>

              {/* Grievance Detail */}
              {detail?.detail ? (
                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Grievance Detail</p>
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                    {detail.detail}
                  </div>
                </div>
              ) : (
                <div className="bg-slate-50 rounded-xl p-4 text-center text-xs text-slate-400 italic">
                  No grievance detail available
                </div>
              )}

              {/* Last Comment */}
              {detail?.last_comment && (
                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Last Comment</p>
                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                    {detail.last_comment}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Status badge ───────────────────────────────────────────
function SBadge({ status }) {
  const key = (status||'').toLowerCase().replace(/[\s-]/g,'_');
  const cfg = STATUS_CFG[key] || STATUS_CFG.new;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

// ── Status card — coloured background tint ────────────────
function StatusCard({ statusKey, data }) {
  const cfg = STATUS_CFG[statusKey]; if (!cfg) return null;
  const Icon = cfg.Icon;
  // Convert hex colour to a very light rgba tint for the card background
  const hexToRgba = (hex, a) => {
    const r=parseInt(hex.slice(1,3),16), g=parseInt(hex.slice(3,5),16), b=parseInt(hex.slice(5,7),16);
    return `rgba(${r},${g},${b},${a})`;
  };
  return (
    <div className="rounded-2xl p-4 border border-opacity-30"
      style={{
        background: hexToRgba(cfg.color, 0.08),
        borderColor: hexToRgba(cfg.color, 0.25),
        borderTopWidth: 4,
        borderTopColor: cfg.color,
      }}>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{background: hexToRgba(cfg.color, 0.15)}}>
          <Icon size={15} style={{color:cfg.color}} />
        </div>
        <span className="text-sm font-bold" style={{color:cfg.color}}>{cfg.label}</span>
      </div>
      <p className="text-[11px] text-slate-500 mb-3 leading-tight">{cfg.desc}</p>
      <div className="grid grid-cols-3 divide-x" style={{borderColor: hexToRgba(cfg.color, 0.2)}}>
        {[['Today',data?.today],['Last 7 Days',data?.week],['Last 30 days',data?.month]].map(([l,v])=>(
          <div key={l} className="text-center px-1">
            <p className="text-xl font-bold" style={{color: v>0 ? cfg.color : '#94a3b8'}}>{v??0}</p>
            <p className="text-[10px] text-slate-400 mt-0.5 leading-tight">{l}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Recharts tooltip ───────────────────────────────────────
function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map(p=>(
        <div key={p.dataKey} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{background:p.fill}} />
          <span className="text-slate-600">{p.name}:</span>
          <span className="font-bold text-slate-800">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════
// ── Telegram Panel (super admin only) ─────────────────────
const DAYS_OF_WEEK = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function TelegramPanel() {
  const [open,            setOpen]            = useState(false);
  const [tab,             setTab]             = useState('recipients');
  const [sending,         setSending]         = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [savingIndividual,setSavingIndividual] = useState(false);
  const [savingChat,      setSavingChat]       = useState(false);
  const [schedule,        setSchedule]         = useState({ enabled:false, time:'09:00', days:['Mon','Tue','Wed','Thu','Fri','Sat'] });
  const [indSchedule,     setIndSchedule]      = useState({ enabled:false, time:'10:00', days:['Mon','Tue','Wed','Thu','Fri','Sat'] });
  const [recipients,      setRecipients]       = useState(null);
  const [chatIdDraft,     setChatIdDraft]      = useState('');
  const [botInfo,         setBotInfo]          = useState(null);   // { username, startLink }
  const [editingAssignee, setEditingAssignee]  = useState({});
  const [sendingAssignee, setSendingAssignee]  = useState({});

  useEffect(() => {
    grievancesAPI.getTelegramSchedule().then(r => setSchedule(r.data)).catch(() => {});
    grievancesAPI.getIndividualSchedule().then(r => setIndSchedule(r.data)).catch(() => {});
    grievancesAPI.getTelegramBotInfo().then(r => setBotInfo(r.data)).catch(() => {});
  }, []);

  const loadRecipients = useCallback(() => {
    grievancesAPI.getTelegramRecipients()
      .then(r => { setRecipients(r.data); setChatIdDraft(r.data.grievanceChatId || ''); })
      .catch(e => console.warn('getTelegramRecipients:', e.response?.data?.error || e.message));
  }, []);

  // Load on mount so the header always shows current chat ID status, even when closed
  useEffect(() => { loadRecipients(); }, [loadRecipients]);

  // Also reload whenever panel opens (to pick up any config changes)
  useEffect(() => { if (open) loadRecipients(); }, [open, loadRecipients]);

  const handleSendNow = async () => {
    setSending(true);
    try {
      const r = await grievancesAPI.sendTelegram();
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Telegram send failed — check Bot Token in School Config → Telegram Bot');
    } finally { setSending(false); }
  };

  const handleSaveSchedule = async () => {
    setSaving(true);
    try {
      const r = await grievancesAPI.saveTelegramSchedule(schedule);
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save schedule');
    } finally { setSaving(false); }
  };

  const handleSaveChatId = async () => {
    setSavingChat(true);
    try {
      const r = await grievancesAPI.saveTelegramChatId({ chatId: chatIdDraft.trim() });
      toast.success(r.data.message);
      loadRecipients();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save chat ID');
    } finally { setSavingChat(false); }
  };

  const toggleDay    = d => setSchedule(s => ({...s, days: s.days.includes(d)?s.days.filter(x=>x!==d):[...s.days,d]}));
  const toggleIndDay = d => setIndSchedule(s => ({...s, days: s.days.includes(d)?s.days.filter(x=>x!==d):[...s.days,d]}));

  const handleSaveIndividualSchedule = async () => {
    setSavingIndividual(true);
    try {
      const r = await grievancesAPI.saveIndividualSchedule(indSchedule);
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save individual schedule');
    } finally { setSavingIndividual(false); }
  };

  // Per-assignee: save their telegram chat ID inline
  const handleSaveAssigneeChatId = async (empId) => {
    const chatId = editingAssignee[empId] ?? '';
    try {
      await grievancesAPI.updateAssigneeChatId(empId, { chatId });
      toast.success('Chat ID saved');
      setEditingAssignee(prev => { const next={...prev}; delete next[empId]; return next; });
      loadRecipients();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save chat ID');
    }
  };

  // Per-assignee: send alert to one person
  const handleSendAssignee = async (empId) => {
    setSendingAssignee(p => ({...p, [empId]: true}));
    try {
      const r = await grievancesAPI.sendAssigneeAlert(empId);
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to send alert');
    } finally { setSendingAssignee(p => ({...p, [empId]: false})); }
  };

  const activeChatId = recipients?.grievanceChatId || recipients?.mgmtChatId;

  return (
    <div className="glass-card overflow-hidden">
      {/* Header row */}
      <div className="flex items-center justify-between px-5 py-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Send size={13} className="text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700">Telegram — Unresolved Grievances Alert</p>
            <p className="text-xs text-slate-400">
              {schedule.enabled
                ? `Scheduled: ${schedule.time} · ${schedule.days.join(', ')}`
                : 'Manual send only'}
              {activeChatId ? ` · Chat: ${activeChatId}` : ' · ⚠️ No chat ID set'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSendNow} disabled={sending}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-60 shadow-sm">
            {sending ? <Loader2 size={12} className="animate-spin"/> : <Send size={12}/>}
            {sending ? 'Sending…' : 'Send Now'}
          </button>
          <button onClick={() => setOpen(o=>!o)}
            className="flex items-center gap-1 px-2.5 py-1.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-500 text-xs rounded-lg transition-colors">
            <Settings2 size={13}/>
            {open ? <ChevronUp size={11}/> : <ChevronDown size={11}/>}
          </button>
        </div>
      </div>

      {/* Settings panel */}
      {open && (
        <div className="border-t border-slate-100 bg-slate-50/40">
          {/* Tab bar */}
          <div className="flex border-b border-slate-100 px-5">
            {[['recipients','Recipients & Chat ID'],['schedule','Schedule']].map(([key,label])=>(
              <button key={key} onClick={()=>setTab(key)}
                className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${
                  tab===key ? 'border-blue-500 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}>{label}</button>
            ))}
          </div>

          {/* ── Recipients Tab ── */}
          {tab === 'recipients' && (
            <div className="px-5 py-4 space-y-5">

              {/* Step-by-step guide */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-2">
                <p className="text-xs font-bold text-blue-800 uppercase tracking-wide">How to get a Telegram Chat ID</p>
                <ol className="text-xs text-blue-700 space-y-1.5 list-none">
                  <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] font-bold flex-shrink-0 flex items-center justify-center">1</span><span>Open Telegram and search for <strong>@userinfobot</strong></span></li>
                  <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] font-bold flex-shrink-0 flex items-center justify-center">2</span><span>Send <code className="bg-blue-100 px-1 rounded">/start</code> — it replies with your <strong>Chat ID</strong> (a number like <code className="bg-blue-100 px-1 rounded">358853050</code>)</span></li>
                  <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] font-bold flex-shrink-0 flex items-center justify-center">3</span><span>For a <strong>group</strong>: add <strong>@userinfobot</strong> to the group → type <code className="bg-blue-100 px-1 rounded">/start</code> → the group chat ID starts with <code className="bg-blue-100 px-1 rounded">-100…</code></span></li>
                  <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] font-bold flex-shrink-0 flex items-center justify-center">4</span><span>Make sure the school's Telegram bot is also a member of that group and has permission to send messages</span></li>
                </ol>
              </div>

              {/* Grievance-specific chat ID */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Grievance Alerts Chat ID</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      All unresolved grievances reports go here.
                      {recipients?.mgmtChatId && !recipients?.grievanceChatId
                        ? ` Falls back to Management Chat (${recipients.mgmtChatId}) if empty.`
                        : ''}
                    </p>
                  </div>
                  {recipients?.grievanceChatId && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Configured ✓</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    value={chatIdDraft}
                    onChange={e => setChatIdDraft(e.target.value)}
                    placeholder="e.g. 358853050  or  -100123456789 (group)"
                    className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono"
                  />
                  <button onClick={handleSaveChatId} disabled={savingChat}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60 flex items-center gap-1.5">
                    {savingChat ? <Loader2 size={13} className="animate-spin"/> : null}
                    {savingChat ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>

              {/* Assignee list with per-person Send + inline chat-ID editor */}
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-700">Assignees — Individual Alerts</p>

                {/* Two-option guide — how to message WITHOUT the /start barrier */}
                <div className="space-y-2">

                  {/* Option A — Group (Recommended) */}
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
                    <p className="text-xs font-bold text-emerald-800">
                      ✅ Option A — Telegram Group <span className="font-normal">(recommended, no /start needed)</span>
                    </p>
                    <ol className="text-xs text-emerald-700 space-y-1 list-none">
                      <li className="flex gap-1.5"><span className="font-bold w-4">1.</span><span>Create a Telegram group (e.g. "TKS Grievance Team")</span></li>
                      <li className="flex gap-1.5"><span className="font-bold w-4">2.</span><span>Add all grievance assignees to this group</span></li>
                      <li className="flex gap-1.5"><span className="font-bold w-4">3.</span>
                        <span>Add the school bot <strong>{botInfo ? `@${botInfo.username}` : '@YourBot'}</strong> as a member and allow it to post</span>
                      </li>
                      <li className="flex gap-1.5"><span className="font-bold w-4">4.</span><span>Get the group Chat ID from <strong>@userinfobot</strong> (starts with <code className="bg-emerald-100 px-1 rounded">-100…</code>)</span></li>
                      <li className="flex gap-1.5"><span className="font-bold w-4">5.</span><span>Enter that group Chat ID in the <strong>Grievance Alerts Chat ID</strong> field above — done</span></li>
                    </ol>
                  </div>

                  {/* Option B — Deep-link (individual) */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2">
                    <p className="text-xs font-bold text-blue-800">
                      🔗 Option B — Individual chat via one-time activation link
                    </p>
                    <p className="text-xs text-blue-700">
                      Share this link with each assignee. They click it <strong>once</strong> in any browser
                      or Telegram — it opens the bot and registers them. After that, Send Alert will work.
                    </p>
                    {botInfo ? (
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs bg-white border border-blue-200 rounded-lg px-3 py-2 text-blue-700 font-mono break-all">
                          {botInfo.startLink}
                        </code>
                        <button
                          onClick={() => { navigator.clipboard.writeText(botInfo.startLink); toast.success('Link copied!'); }}
                          className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded-lg transition-colors flex-shrink-0"
                        >
                          Copy
                        </button>
                      </div>
                    ) : (
                      <p className="text-xs text-blue-500 italic">Loading bot info…</p>
                    )}
                    <p className="text-xs text-blue-600">
                      After the assignee clicks the link → open <strong>@userinfobot</strong> to get their Chat ID → enter it below.
                    </p>
                  </div>
                </div>
                {recipients?.assignees?.length === 0 && (
                  <p className="text-xs text-slate-400 italic py-2">No unresolved grievances with assignees in current academic year</p>
                )}
                {(recipients?.assignees || []).map(a => {
                  const isEditing  = editingAssignee[a.emp_id] !== undefined;
                  const draftValue = isEditing ? editingAssignee[a.emp_id] : (a.telegram_chat_id || '');
                  const isSending  = sendingAssignee[a.emp_id];

                  return (
                    <div key={a.emp_id || a.name} className={`rounded-xl border p-3 space-y-2 ${
                      a.telegram_chat_id ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
                    }`}>
                      {/* Top row: name + send button */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-700">{a.name}</p>
                          <p className="text-xs text-slate-500">
                            {a.grievance_count} open {a.grievance_count!==1?'grievances':'grievance'}
                          </p>
                        </div>
                        <button
                          onClick={() => handleSendAssignee(a.emp_id)}
                          disabled={!a.telegram_chat_id || isSending}
                          title={!a.telegram_chat_id ? 'Enter Chat ID first' : `Send alert to ${a.name}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                        >
                          {isSending ? <Loader2 size={11} className="animate-spin"/> : <Send size={11}/>}
                          {isSending ? 'Sending…' : 'Send Alert'}
                        </button>
                      </div>

                      {/* Chat ID row */}
                      <div className="flex items-center gap-2">
                        {isEditing ? (
                          <>
                            <input
                              autoFocus
                              value={draftValue}
                              onChange={e => setEditingAssignee(p => ({...p, [a.emp_id]: e.target.value}))}
                              placeholder="Enter Telegram Chat ID"
                              className="flex-1 text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
                              onKeyDown={e => { if(e.key==='Enter') handleSaveAssigneeChatId(a.emp_id); if(e.key==='Escape') setEditingAssignee(p=>{const n={...p};delete n[a.emp_id];return n;}); }}
                            />
                            <button onClick={() => handleSaveAssigneeChatId(a.emp_id)}
                              className="px-2.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition-colors">
                              Save
                            </button>
                            <button onClick={() => setEditingAssignee(p=>{const n={...p};delete n[a.emp_id];return n;})}
                              className="px-2.5 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-600 text-xs rounded-lg transition-colors">
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <span className={`flex-1 text-xs font-mono px-2.5 py-1.5 rounded-lg ${
                              a.telegram_chat_id
                                ? 'text-emerald-700 bg-emerald-100'
                                : 'text-amber-600 bg-amber-100 italic'
                            }`}>
                              {a.telegram_chat_id || 'No Chat ID set'}
                            </span>
                            <button
                              onClick={() => setEditingAssignee(p => ({...p, [a.emp_id]: a.telegram_chat_id || ''}))}
                              className="px-2.5 py-1.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-500 text-xs rounded-lg transition-colors">
                              {a.telegram_chat_id ? 'Edit' : 'Set ID'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Schedule Tab ── */}
          {tab === 'schedule' && (
            <div className="px-5 py-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-700">Scheduled Alerts</p>
                  <p className="text-xs text-slate-400 mt-0.5">Automatically send unresolved grievances report</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={schedule.enabled}
                    onChange={e => setSchedule(s=>({...s, enabled:e.target.checked}))} />
                  <div className="w-10 h-5 bg-slate-200 rounded-full peer
                    peer-checked:after:translate-x-5 peer-checked:bg-blue-500
                    after:content-[''] after:absolute after:top-0.5 after:left-0.5
                    after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all" />
                </label>
              </div>

              {schedule.enabled && (
                <>
                  <div className="flex items-center gap-4">
                    <label className="text-xs font-medium text-slate-500 w-24">Send Time</label>
                    <input type="time" value={schedule.time}
                      onChange={e => setSchedule(s=>({...s, time:e.target.value}))}
                      className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs font-medium text-slate-500 w-24">Days</span>
                    {DAYS_OF_WEEK.map(d => (
                      <button key={d} onClick={() => toggleDay(d)}
                        className={`w-10 h-10 rounded-full text-xs font-semibold border transition-all ${
                          schedule.days.includes(d)
                            ? 'bg-blue-500 text-white border-blue-500 shadow-sm'
                            : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'
                        }`}>
                        {d}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <div className="flex justify-end pt-1">
                <button onClick={handleSaveSchedule} disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60">
                  {saving ? <Loader2 size={13} className="animate-spin"/> : null}
                  {saving ? 'Saving…' : 'Save Summary Schedule'}
                </button>
              </div>

              {/* Divider */}
              <hr className="border-slate-200" />

              {/* Individual Assignee Alerts Schedule */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-700">Individual Assignee Alerts</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Send personal reminders to each assignee about their own open grievances
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={indSchedule.enabled}
                    onChange={e => setIndSchedule(s=>({...s, enabled:e.target.checked}))} />
                  <div className="w-10 h-5 bg-slate-200 rounded-full peer
                    peer-checked:after:translate-x-5 peer-checked:bg-emerald-500
                    after:content-[''] after:absolute after:top-0.5 after:left-0.5
                    after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all" />
                </label>
              </div>

              {indSchedule.enabled && (
                <>
                  <div className="flex items-center gap-4">
                    <label className="text-xs font-medium text-slate-500 w-24">Send Time</label>
                    <input type="time" value={indSchedule.time}
                      onChange={e => setIndSchedule(s=>({...s, time:e.target.value}))}
                      className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs font-medium text-slate-500 w-24">Days</span>
                    {DAYS_OF_WEEK.map(d => (
                      <button key={d} onClick={() => toggleIndDay(d)}
                        className={`w-10 h-10 rounded-full text-xs font-semibold border transition-all ${
                          indSchedule.days.includes(d)
                            ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm'
                            : 'bg-white text-slate-500 border-slate-200 hover:border-emerald-300'
                        }`}>
                        {d}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <div className="flex justify-end pt-1">
                <button onClick={handleSaveIndividualSchedule} disabled={savingIndividual}
                  className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60">
                  {savingIndividual ? <Loader2 size={13} className="animate-spin"/> : null}
                  {savingIndividual ? 'Saving…' : 'Save Individual Schedule'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const GRIEVANCE_TABS = [
  { id: 'dashboard',  label: 'Dashboard',  icon: LayoutDashboard },
  { id: 'grievances', label: 'Grievances', icon: List             },
  { id: 'setup',      label: 'Setup',      icon: Settings2        },
];

export default function Grievances() {
  const { yearId } = useContext(YearContext);
  const { isAdmin, isSuperAdmin } = useAuth();
  const canSetup = isAdmin || isSuperAdmin;
  const [tab,          setTab]          = useState('dashboard');
  const [filter,       setFilter]       = useState(defaultRange);
  const [data,         setData]         = useState(null);
  const [list,         setList]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [listLoading,  setListLoading]  = useState(false);
  const [modalId,      setModalId]      = useState(null);

  // ── List tab filter state ───────────────────────────────
  const [listPreset,     setListPreset]     = useState('current_month');
  const [listDateParams, setListDateParams] = useState(() => getPresetDates('This Month'));
  const [customStart,    setCustomStart]    = useState('');
  const [customEnd,      setCustomEnd]      = useState('');
  const [search,         setSearch]         = useState('');

  const load = useCallback(() => {
    setLoading(true);
    grievancesAPI.getDashboardStats({ creationStart: filter.start, creationEnd: filter.end })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filter]);

  const loadList = useCallback(() => {
    setListLoading(true);
    grievancesAPI.getList({ startDate: listDateParams.start, endDate: listDateParams.end, limit: 500 })
      .then(r => setList(r.data?.data || r.data || []))
      .catch(console.error)
      .finally(() => setListLoading(false));
  }, [listDateParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadList(); }, [loadList]);

  const filteredList = useMemo(() => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(row =>
      [row.grievance_no, row.student_name, row.category, row.assignee, row.class_section]
        .some(v => (v || '').toLowerCase().includes(q))
    );
  }, [list, search]);

  if (loading && tab !== 'setup') return <PageLoader />;

  const {
    statusCards={}, byCategory=[], byStatus=[], byPriority=[],
    latest5=[], unresolvedRecent=[], byClass=[],
  } = data || {};

  const totalStatus   = byStatus.reduce((s,r)=>s+(r.cnt||0),0);
  const totalPriority = byPriority.reduce((s,r)=>s+(r.cnt||0),0);

  // Bar series — defined here so Recharts can see them as direct <Bar> elements
  const statusBars = Object.entries(BAR_COLORS).map(([k,c])=>(
    <Bar key={k} dataKey={k} name={STATUS_CFG[k]?.label||k} fill={c} radius={[3,3,0,0]} maxBarSize={30} />
  ));

  const listColumns = [
    { key: 'grievance_no',  label: 'No.',          sortable: true },
    { key: 'category',      label: 'Category',     sortable: true },
    { key: 'student_name',  label: 'Student',      sortable: true },
    { key: 'class_section', label: 'Class',        sortable: true },
    { key: 'assignee',      label: 'Assignee',     sortable: true },
    { key: 'priority',      label: 'Priority',     sortable: true,
      render: v => {
        const colours = { low:'text-emerald-600', medium:'text-blue-600', high:'text-orange-600', critical:'text-red-600' };
        return <span className={`text-xs font-semibold capitalize ${colours[v]||'text-slate-600'}`}>{v||'—'}</span>;
      }
    },
    { key: 'status',        label: 'Status',       render: v => <SBadge status={v} /> },
    { key: 'raised_date',   label: 'Date',         sortable: true, render: v => fmtDate(v) },
  ];

  function applyListPreset(preset) {
    setListPreset(preset);
    if (preset === 'today')          setListDateParams(getPresetDates('Today'));
    else if (preset === 'current_month')  setListDateParams(getPresetDates('This Month'));
    else if (preset === 'previous_month') setListDateParams(getPresetDates('Last Month'));
    // 'custom' — user must fill dates and click Apply
  }

  const visibleTabs = GRIEVANCE_TABS.filter(t => t.id !== 'setup' || canSetup);

  return (
    <div className="space-y-5 animate-fade-in">

      {/* ── Page header with tab navigation ── */}
      <div className="space-y-2.5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-display font-bold text-slate-800">Grievance Summary</h2>
            <p className="text-slate-500 text-sm mt-0.5">Track, analyse and manage all school grievances</p>
          </div>

          {/* Tab switcher */}
          <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm shrink-0">
            {visibleTabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium transition-colors ${
                  tab === t.id ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}>
                <t.icon size={13} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Second row — Import/Export (grievances tab only) */}
        {tab === 'grievances' && (
          <div className="flex justify-end">
            <ImportExport
              compact
              module="Grievances"
              onImport={fd  => grievancesAPI.import(fd)}
              onExport={() => grievancesAPI.export({ startDate: listDateParams.start, endDate: listDateParams.end })}
              onSampleTemplate={grievancesAPI.sampleTemplate}
              onSuccess={loadList}
            />
          </div>
        )}
      </div>

      {/* ── Grievances list tab ────────────────────────────────── */}
      {tab === 'grievances' && (
        <>
          {/* Filter bar */}
          <div className="bg-white rounded-xl px-4 py-3 shadow-card border border-slate-100 flex flex-wrap items-center gap-3">

            {/* Preset buttons */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {[
                ['today',          'Today'],
                ['current_month',  'Current Month'],
                ['previous_month', 'Previous Month'],
                ['custom',         'Custom'],
              ].map(([id, label]) => (
                <button key={id} onClick={() => applyListPreset(id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap ${
                    listPreset === id
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-primary-300 hover:text-primary-600'
                  }`}>
                  {label}
                </button>
              ))}
            </div>

            {/* Custom date inputs + Apply */}
            {listPreset === 'custom' && (
              <div className="flex items-center gap-2">
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
                <span className="text-slate-400 text-sm">to</span>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
                <button
                  disabled={!customStart || !customEnd}
                  onClick={() => setListDateParams({ start: customStart, end: customEnd })}
                  className="px-4 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  Apply
                </button>
              </div>
            )}

            {/* Search */}
            <div className="flex-1 min-w-[220px] relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search no., student, category, assignee…"
                className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>

          <DataTable
            columns={listColumns}
            data={filteredList}
            loading={listLoading}
            emptyText="No grievances found for the selected period"
            onRowClick={row => setModalId(row.id)}
          />
        </>
      )}

      {/* ── Dashboard tab ─────────────────────────────────────── */}
      {tab === 'dashboard' && <>

      {/* ── Status Cards ── */}
      <div className="glass-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-sm font-bold text-slate-700">Grievance Summary</h3>
          <span className="text-xs text-slate-400">— all-time counts by status</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {Object.keys(STATUS_CFG).map(k=>(
            <StatusCard key={k} statusKey={k} data={statusCards[k]||{today:0,week:0,month:0}} />
          ))}
        </div>
      </div>

      {/* ── Global Date Range Picker — between Summary and charts ── */}
      <div className="glass-card px-5 py-3">
        <DateRangePicker value={filter} onChange={setFilter} />
      </div>

      {/* ── Grievances by Category ── */}
      <div className="glass-card p-5">
        <h3 className="text-sm font-bold text-slate-700 mb-4">Grievances by Category</h3>
        {byCategory.length===0
          ? <p className="text-sm text-slate-400 text-center py-8">No data for selected period</p>
          : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={byCategory} margin={{top:5,right:20,left:0,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="category" tick={{fontSize:11,fill:'#64748b'}} />
                <YAxis tick={{fontSize:11,fill:'#64748b'}} allowDecimals={false} />
                <Tooltip content={<ChartTip/>} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:11}} />
                {statusBars}
              </BarChart>
            </ResponsiveContainer>
          )
        }
      </div>

      {/* ── Report by Status + Priority donuts ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        <div className="glass-card p-5">
          <h3 className="text-sm font-bold text-slate-700 mb-4">Report by Status</h3>
          {byStatus.length===0
            ? <p className="text-sm text-slate-400 text-center py-8">No data</p>
            : (
              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-2.5 min-w-[140px]">
                  {byStatus.map(s=>{
                    const key=(s.status||'').toLowerCase().replace(/[\s-]/g,'_');
                    return (
                      <div key={s.status} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{background:BAR_COLORS[key]||'#94a3b8'}} />
                          <span className="text-xs text-slate-600">{s.status}</span>
                        </div>
                        <span className="text-xs font-bold text-slate-800">{s.cnt}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex-1 relative">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={byStatus} dataKey="cnt" nameKey="status" cx="50%" cy="50%" innerRadius={55} outerRadius={85}>
                        {byStatus.map(s=>{
                          const key=(s.status||'').toLowerCase().replace(/[\s-]/g,'_');
                          return <Cell key={s.status} fill={BAR_COLORS[key]||'#94a3b8'} />;
                        })}
                      </Pie>
                      <Tooltip formatter={(v,n)=>[v,n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-2xl font-bold text-slate-800">{totalStatus}</span>
                    <span className="text-xs text-slate-400">Total</span>
                  </div>
                </div>
              </div>
            )
          }
        </div>

        <div className="glass-card p-5">
          <h3 className="text-sm font-bold text-slate-700 mb-4">Report by Priority</h3>
          {byPriority.length===0
            ? <p className="text-sm text-slate-400 text-center py-8">No data</p>
            : (
              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-2.5 min-w-[140px]">
                  {byPriority.map(p=>(
                    <div key={p.priority} className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{background:PRIO_COLORS[p.priority]||'#94a3b8'}} />
                        <span className="text-xs text-slate-600 capitalize">{PRIO_LABELS[p.priority]||p.priority}</span>
                      </div>
                      <span className="text-xs font-bold text-slate-800">{p.cnt}</span>
                    </div>
                  ))}
                </div>
                <div className="flex-1 relative">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={byPriority} dataKey="cnt" nameKey="priority" cx="50%" cy="50%" innerRadius={55} outerRadius={85}>
                        {byPriority.map(p=>(
                          <Cell key={p.priority} fill={PRIO_COLORS[p.priority]||'#94a3b8'} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v,n)=>[v,n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-2xl font-bold text-slate-800">{totalPriority}</span>
                    <span className="text-xs text-slate-400">Total</span>
                  </div>
                </div>
              </div>
            )
          }
        </div>
      </div>

      {/* ── Issue Status by Class (date-filtered) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        <div className="glass-card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-700">Issue Status by Category</h3>
          </div>
          {byClass.length===0
            ? <p className="text-sm text-slate-400 text-center py-8">No data</p>
            : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                      <th className="px-4 py-2.5 text-left font-medium">Class</th>
                      {Object.values(STATUS_CFG).map(s=>(
                        <th key={s.label} className="px-3 py-2.5 text-center font-medium">{s.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {byClass.map(row=>(
                      <tr key={row.class_label} className="hover:bg-slate-50/60">
                        <td className="px-4 py-2.5 font-medium text-slate-700">{row.class_label}</td>
                        {[row.new,row.in_progress,row.reopened,row.resolved,row.closed].map((v,i)=>(
                          <td key={i} className="px-3 py-2.5 text-center text-slate-600">{v||0}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>

        <div className="glass-card p-5">
          <h3 className="text-sm font-bold text-slate-700 mb-4">Issue Status by Category Report</h3>
          {byClass.length===0
            ? <p className="text-sm text-slate-400 text-center py-8">No data</p>
            : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={byClass} margin={{top:5,right:10,left:0,bottom:5}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="class_label" tick={{fontSize:10,fill:'#64748b'}} />
                  <YAxis tick={{fontSize:10,fill:'#64748b'}} allowDecimals={false} />
                  <Tooltip content={<ChartTip/>} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:10}} />
                  {statusBars}
                </BarChart>
              </ResponsiveContainer>
            )
          }
        </div>
      </div>

      {/* ── Latest 5 + Unresolved — last row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Latest 5 — clickable */}
        <div className="glass-card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-700">Latest 5 Grievances</h3>
            <p className="text-xs text-slate-400 mt-0.5">All-time most recent — click a row for details</p>
          </div>
          {latest5.length===0
            ? <p className="text-sm text-slate-400 text-center py-10">No grievances yet</p>
            : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                    <th className="px-4 py-2.5 text-left font-medium">Grievance No.</th>
                    <th className="px-4 py-2.5 text-left font-medium">Category</th>
                    <th className="px-4 py-2.5 text-left font-medium">Assignee</th>
                    <th className="px-4 py-2.5 text-left font-medium">Date</th>
                    <th className="px-4 py-2.5 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {latest5.map(g=>(
                    <tr key={g.id} onClick={()=>setModalId(g.id)}
                      className="hover:bg-primary-50/40 cursor-pointer transition-colors group">
                      <td className="px-4 py-2.5">
                        <span className="text-primary-600 font-mono font-medium text-[11px] flex items-center gap-1 group-hover:underline">
                          {g.grievance_no||`#${g.id}`}
                          <ExternalLink size={10} className="opacity-0 group-hover:opacity-60" />
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">{g.category||'—'}</td>
                      <td className="px-4 py-2.5 text-slate-600">{g.assignee||'—'}</td>
                      <td className="px-4 py-2.5 text-slate-500">{fmtDate(g.raised_date)}</td>
                      <td className="px-4 py-2.5"><SBadge status={g.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>

        {/* Unresolved — all non-closed, clickable */}
        <div className="glass-card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-slate-700">Unresolved Grievances</h3>
              <p className="text-xs text-slate-400 mt-0.5">Current academic year (Apr – Mar) · click for details</p>
            </div>
            {unresolvedRecent.length>0 && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                {unresolvedRecent.length} open
              </span>
            )}
          </div>
          {unresolvedRecent.length===0
            ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <CheckCircle2 size={32} className="text-slate-300" />
                <p className="text-sm text-slate-400 font-medium">No data available</p>
                <p className="text-xs text-slate-300">All grievances are closed</p>
              </div>
            )
            : (
              <div className="divide-y divide-slate-50 max-h-80 overflow-y-auto">
                {unresolvedRecent.map(g=>(
                  <div key={g.id} onClick={()=>setModalId(g.id)}
                    className="px-5 py-3 hover:bg-primary-50/40 flex items-start gap-3 cursor-pointer group transition-colors">
                    <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                      style={{background:STATUS_CFG[g.status]?.color||'#94a3b8'}} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-mono text-primary-600 font-medium flex items-center gap-1 group-hover:underline">
                        {g.grievance_no||`#${g.id}`}
                        <ExternalLink size={10} className="opacity-0 group-hover:opacity-60" />
                      </p>
                      <p className="text-xs text-slate-700 truncate mt-0.5">{g.subject}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{g.category} · {fmtDate(g.raised_date)}</p>
                    </div>
                    <SBadge status={g.status} />
                  </div>
                ))}
              </div>
            )
          }
        </div>
      </div>

      </>} {/* end dashboard tab */}

      {/* ── Setup tab ─────────────────────────────────────────── */}
      {tab === 'setup' && canSetup && (
        <div className="space-y-5">
          {isSuperAdmin && <TelegramPanel />}
          <EmailNotificationSetup module="grievances" moduleLabel="Grievances" yearId={yearId} />
        </div>
      )}

      {/* ── Grievance Detail Modal (shared across both tabs) ── */}
      {modalId !== null && (
        <GrievanceModal grievanceId={modalId} onClose={()=>setModalId(null)} />
      )}

    </div>
  );
}
